import type { BlockDefinition, VariableMeta } from '@tsagko/label-contracts'
import { useMemo, useState } from 'react'
import { VariableField } from './VariableField'

interface BlockPropsModalProps {
  block: BlockDefinition
  /** When provided, the modal is in "edit" mode: fields are seeded from these
   *  instance props and the title/submit copy changes accordingly. */
  existingProps?: Record<string, string>
  onCancel: () => void
  onSubmit: (values: Record<string, string>) => void
}

/**
 * Seed initial field values for the modal.
 *
 * Seeding semantics:
 *  - If `existingProps` is supplied (edit mode): for each variable, use
 *    `existingProps[name]` verbatim when the key IS present — even if the
 *    value is an empty string (an operator may have intentionally blanked a
 *    field). Only when the key is ABSENT does the variable's declared default
 *    fill in.
 *  - If `existingProps` is absent (drop mode): seed from variable defaults
 *    only, exactly as before.
 */
function initialValues(
  variables: Record<string, VariableMeta>,
  existingProps?: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [name, meta] of Object.entries(variables)) {
    // existingProps present AND key exists → use verbatim (even '')
    if (existingProps !== undefined && Object.hasOwn(existingProps, name)) {
      // Object.hasOwn just confirmed the key exists — noUncheckedIndexedAccess
      // still widens the index read to `string | undefined`.
      out[name] = existingProps[name]!
      continue
    }
    // Otherwise fall through to the declared default
    if (meta.type === 'enum') {
      out[name] = meta.default ?? meta.enumOptions?.[0] ?? ''
    } else if (meta.type === 'boolean') {
      out[name] = meta.default ?? 'false'
    } else {
      out[name] = meta.default ?? ''
    }
  }
  return out
}

export function BlockPropsModal({ block, existingProps, onCancel, onSubmit }: BlockPropsModalProps) {
  // A migrated BlockDefinition may have `variables: undefined` when the block
  // declares none. Coerce once so callers don't sprinkle `?? {}` everywhere.
  const variables = useMemo<Record<string, VariableMeta>>(
    () => block.variables ?? {},
    [block.variables]
  )
  const isEditMode = existingProps !== undefined
  const [values, setValues] = useState<Record<string, string>>(
    () => initialValues(variables, existingProps)
  )
  const entries = useMemo(() => Object.entries(variables), [variables])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="bake-modal"
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-stone-900"
      >
        <h3 className="mb-1 text-base font-semibold">
          {isEditMode ? 'Edit values' : `Drop ${block.name}`}
        </h3>
        <p className="mb-3 text-xs text-stone-500">
          {isEditMode
            ? 'Edit the values for this block instance. Blank fields stay blank.'
            : 'Provide a value for each variable. Leave a field blank to use its declared default.'}
        </p>
        <div className="space-y-3">
          {entries.length === 0 && (
            <p className="text-xs text-stone-400">
              This block has no inputs — it will be dropped as-is.
            </p>
          )}
          {entries.map(([name, meta]) => (
            <VariableField
              key={name}
              name={name}
              meta={meta}
              value={values[name] ?? ''}
              onChange={next => setValues({ ...values, [name]: next })}
            />
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-stone-300 px-3 py-1 text-sm text-stone-700 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="bake-modal-submit"
            onClick={() => onSubmit(values)}
            className="rounded bg-sky-600 px-3 py-1 text-sm text-white disabled:opacity-40 hover:enabled:bg-sky-700"
          >
            {isEditMode ? 'Save values' : 'Drop block'}
          </button>
        </div>
      </div>
    </div>
  )
}
